import React, { Component } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import moment from 'moment-timezone';
import * as Routes from 'routes';
import { clsx } from 'clsx';
import I18n from 'i18n-js';
import showToast from 'common/Toast';
import { pollEndpoint } from '@/modules/TCIUtils';
import useFlags from '@/hooks/useFlags';
import { GradeByType } from '@/components/staffer/NotebookGradeByMenu/types';
import { LTI_KINDS, lmsProviderConfig } from '@/components/shared/Lms/lmsProviders';
import { Footer } from '../../common/Modal/index';
import { renderErrors, SubmitError } from '../../common/Forms/Utils';
import googleClassroomLogo from '../classrooms/Table/ConnectClassroom/google_logo.svg';
import canvasLogo from '../classrooms/Table/ConnectClassroom/canvas_logo.svg';
import Banner from '../../common/Banner';
import WhatSection from './WhatSection';
import WhenSection from './WhenSection';
import WhoSection from './WhoSection';
import NotesSection from './NotesSections';
import {
  classroomsPropType, assessmentsPropType,
  assignmentInitialValuesPropType
} from './PropTypes';
import styles from './AssignmentForm.module.scss';
import { buildAssignmentsModels } from './Utils';
import useAssignablesStore from './WhatSection/store/useAssignablesStore';

export default class AssignmentForm extends Component {
  static propTypes = {
    assessments: assessmentsPropType,
    assessmentsPagePath: PropTypes.string.isRequired,
    assignmentsPath: PropTypes.string.isRequired,
    classrooms: classroomsPropType,
    closeModal: PropTypes.func.isRequired,
    connectedToCanvas: PropTypes.bool,
    connectedToGoogleClassroom: PropTypes.bool,
    games: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        score: PropTypes.number,
        value: PropTypes.string,
      })
    ).isRequired,
    handlePostSave: PropTypes.func.isRequired,
    initialIsUnit: PropTypes.bool,
    initialLessonId: PropTypes.number,
    initialValues: assignmentInitialValuesPropType,
    loadingInitialValues: PropTypes.bool,
    ltiKind: PropTypes.oneOf(LTI_KINDS),
    newRecord: PropTypes.bool,
    program: PropTypes.shape({
      display_investigations: PropTypes.bool.isRequired,
      display_investigations_text_section: PropTypes.bool.isRequired,
      display_unit_content: PropTypes.bool.isRequired,
      hasVideoActivity: PropTypes.bool.isRequired,
      id: PropTypes.number.isRequired,
      middle_school: PropTypes.bool.isRequired,
      science: PropTypes.bool.isRequired
    }).isRequired,
    programId: PropTypes.number,
    score: PropTypes.number,
    stafferId: PropTypes.number.isRequired,
    subscriberConnectedToCanvas: PropTypes.bool,
    updatePath: PropTypes.string
  };

  static defaultProps = {
    assessments: [],
    classrooms: [],
    connectedToCanvas: false,
    connectedToGoogleClassroom: false,
    initialIsUnit: false,
    initialLessonId: null,
    initialValues: {},
    loadingInitialValues: false,
    ltiKind: null,
    newRecord: true,
    score: 0,
    subscriberConnectedToCanvas: false,
    updatePath: ''
  };

  static formHandleSelect(args, state, utils) {
    const value = args[0] === null ? null : args[0].value;
    AssignmentForm.formChangeValue(args, state, utils, value);
  }

  static formChangeValue(args, state, utils, newValue) {
    const value = newValue || args[0];
    utils.changeValue(state, args[1], () => value);
  }

  static validate(values) {
    const errors = {};
    const noModelsToAssign = !(values.modelsToAssign && values.modelsToAssign.length);
    const noNotebookAndReadingToAssign = Object.keys(values)?.filter(key => key.includes('notebook-reading'))?.length === 0;

    if (noModelsToAssign && noNotebookAndReadingToAssign) {
      errors.modelsToAssign = 'Choose at least one item to assign.';
    }

    return errors;
  }

  constructor(props) {
    super(props);

    this.state = {
      showBanner: !!this.props.ltiKind,
    };

    this.renderForm = this.renderForm.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.setShouldShowLmsInfo = this.setShouldShowLmsInfo.bind(this);
  }

  getInitialValues() {
    const initialValues = Object.assign(
      { modelsToAssign: [], lesson_id: this.props.initialLessonId },
      this.props.initialValues
    );

    if (this.props.newRecord) {
      initialValues.classroom_ids = this.props.classrooms.map(classroom => classroom.id.toString());
      initialValues.student_ids = [];
      initialValues.start_datetime = moment().utc().format('YYYY-MM-DD H:mm:ss UTC');
      initialValues.lesson_id = this.props.initialLessonId;
    }
    else if (Object.keys(this.props.initialValues).length) {
      if (this.props.initialValues.classroomAssignment) {
        initialValues.classroom_ids = this.props.initialValues.assignee_model_ids.map(id => id.toString());

        // if a classroom assignment has only been assigned to one classroom, select all students in the student selector
        if (this.props.initialValues.assignee_model_ids.length === 1) {
          initialValues.student_ids = this.props.initialValues.classroom_student_ids.map(id => id.toString());
        }
        else {
          initialValues.student_ids = [];
        }
      }
      else {
        initialValues.classroom_ids = this.props.initialValues.student_classroom_ids.map(id => id.toString());
        // If the assignment has only been assigned to one classroom, populate the student selector with the individual assignees
        if (this.props.initialValues.student_classroom_ids.length === 1) {
          initialValues.student_ids = this.props.initialValues.assignee_model_ids.map(id => id.toString());
        }
        else {
          initialValues.student_ids = [];
        }
      }

      const dueDateTime = moment(this.props.initialValues.due_datetime, 'MM/DD/YYYY, H:mm A');
      initialValues.due_datetime = dueDateTime.utc().format('YYYY-MM-DD H:mm:ss UTC');

      const startDateTime = moment(this.props.initialValues.start_datetime, 'MM/DD/YYYY, H:mm A');
      initialValues.start_datetime = startDateTime.utc().format('YYYY-MM-DD H:mm:ss UTC');
      initialValues.randomize_answers = this.props.initialValues.randomize_answers;
      initialValues.grading_notebook_by = this.props.initialValues.grading_notebook_by;
      initialValues.max_score = this.props.initialValues.max_score;
      initialValues.show_answers_to_students = this.props.initialValues.show_answers_to_students;
    }

    return initialValues;
  }

  setShouldShowLmsInfo(value) {
    this.setState({ showLmsInfo: value });
  }

  handleSubmit(values) {
    const requestData = this.buildRequestData(values);

    if (this.props.newRecord) {
      return axios
        .post(this.props.assignmentsPath, requestData)
        .then((response) => {
          const { data } = response;
          if (data.assessment_copy_in_progress) {
            return this.pollForCompletion(data);
          }

          showToast('Assignment created!');
          this.props.handlePostSave(data.data, 'create');
          this.props.closeModal();
        })
        .catch(error => ({
          [FORM_ERROR]: renderErrors(error.response.data.errors)
        }));
    }

    return axios
      .patch(this.props.updatePath.replace(':id', this.props.initialValues.assignment_id), requestData)
      .then((response) => {
        showToast('Assignment edited!');
        this.props.handlePostSave(response.data.data, 'update');
        this.props.closeModal();
      })
      .catch(error => ({
        [FORM_ERROR]: renderErrors(error.response.data.errors)
      }));
  }

  pollForCompletion(data) {
    return pollEndpoint({
      endpointUrl: Routes.plato_api_assessment_path(data.new_assessment_id),
      interval: 1000,
      until: response => !response.data[Object.keys(response.data)[0]].copying
    })
      .then(() => {
        showToast('Assignment created!');
        this.props.closeModal();
        window.location.reload();
      })
      .catch((response) => {
        if (response.toString().includes('timed out')) {
          showToast('Assignment creation will finish shortly.', { autoClose: 3000 });
        }
        this.props.closeModal();
      });
  }

  formatNotebookAndReading(values) {
    return Object.keys(values)
      .filter(key => key.includes('notebook-reading'))
      .map(key => values[key])
      .filter(val => val && val.length > 0);
  }

  buildRequestData(values) {
    const assignment = {
      dates_enforced: values.dates_enforced,
      due_datetime: values.due_datetime,
      name: values.name,
      notes: values.notes,
      program_id: this.props.programId || this.props.program.id,
      randomize_answers: values.randomize_answers || false,
      show_answers_to_students: values.show_answers_to_students || false,
      start_datetime: values.start_datetime,
    };

    const notebookAndReadingToAssign = this.formatNotebookAndReading(values);

    let formattedModelsToAssign;
    const isSectionNotebook = values.type === 'sectionNotebook';

    const { usesAssignmentsBasedGrading } = useFlags();
    if (isSectionNotebook) {
      const gradingBy = values.grading_notebook_by;
      assignment.grading_notebook_by = gradingBy;
      const maxScore = usesAssignmentsBasedGrading || gradingBy === GradeByType.Assignment ? values.max_score : null;
      assignment.max_score = maxScore;
    }

    // if assigning multiple things
    if (isSectionNotebook && notebookAndReadingToAssign.length > 0) {
      formattedModelsToAssign = notebookAndReadingToAssign;
    }
    else if (Array.isArray(values.modelsToAssign)) {
      formattedModelsToAssign = [...values.modelsToAssign];
    }
    else {
      formattedModelsToAssign = [values.modelsToAssign];
    }

    const currentUnitLesson = useAssignablesStore.getState().selectedUnitLesson;

    return {
      assignment,
      assignments_models: buildAssignmentsModels(
        formattedModelsToAssign.filter(toAssign => toAssign !== undefined).flat(),
        values.lesson_id,
        currentUnitLesson.isUnit,
      ),
      classroom_ids: values.classroom_ids,
      extra_attributes: ['has_sync_errors?', 'pending_publish_on_google?'],
      student_ids: values.student_ids
    };
  }

  shouldRenderDraftMessage() {
    if (!this.props.newRecord) return false;
    if (this.props.connectedToGoogleClassroom || this.props.ltiKind === 'canvas') return true;

    return this.props.subscriberConnectedToCanvas && this.props.connectedToCanvas;
  }

  renderDraftMessage() {
    if (!this.shouldRenderDraftMessage()) return '';

    const articleLink = this.props.connectedToCanvas ?
      'https://www.teachtci.com/canvas-draft-assignments' : 'https://www.teachtci.com/google-draft-assignments';

    return (
      <span className={styles.draftMessage}>
        <img alt="" className={styles.lmsLogo} src={this.props.connectedToCanvas ? canvasLogo : googleClassroomLogo} />

        A draft assignment will be created in your connected {' '}
        {this.props.connectedToCanvas ? 'Canvas Course(s)' : 'Google Classroom(s)'}. {' '}
        <a
          className={styles.lmsLink}
          href={articleLink}
          rel="noopener noreferrer"
          target="_blank"
        >
          Learn More
        </a>
      </span>
    );
  }

  renderForm({
    dirtySinceLastSubmit, errors, form, handleSubmit, hasSubmitErrors,
    hasValidationErrors, initialValues, submitError, submitting
  }) {
    const showBanner = this.state.showBanner;
    const ltiProvider = this.props.ltiKind ? lmsProviderConfig(this.props.ltiKind) : null;

    return (
      <form
        className={styles.form}
        onSubmit={handleSubmit}
      >
        <div className={clsx(styles.sectionsContainer, { 'tw-pt-0': showBanner })}>
          {showBanner && (
            <div className={styles.banner}>
              <Banner
                showCloseButton
                singleColorCard
                borderColor="orange"
                color="yellow"
                extraContentClassName="tw-flex tw-items-center tw-justify-between tw-gap-3"
                iconColor="orange"
                iconType="fa fa-exclamation-triangle"
                onClose={() => { this.setState({ showBanner: false }); }}
              >
                <span>{I18n.t('lti_1_3_assignment_creation_warning', { lms13Title: ltiProvider.name })}</span>
                <a className="tw-ml-1" href={ltiProvider.urls.integration} target="_blank">{I18n.t('learn_more')}</a>
              </Banner>
            </div>
          )}
          <div className={styles.columns}>
            <WhatSection
              form={form}
              formChangeValue={form.mutators.changeValue}
              initialIsUnit={this.props.initialIsUnit}
              initialLessonId={this.props.initialLessonId}
              initialValues={initialValues}
              programID={this.props.programId || this.props.program.id}
              stafferId={this.props.stafferId}
            />
            <div>
              <WhoSection
                classrooms={this.props.classrooms}
                formChangeValue={form.mutators.changeValue}
                initialValues={initialValues}
                loadingInitialValues={this.props.loadingInitialValues}
                setShouldShowLmsInfo={this.setShouldShowLmsInfo}
              />

              <WhenSection
                errors={errors}
                form={form}
                formHandleSelect={form.mutators.handleSelect}
                games={this.props.games}
                initialValues={initialValues}
                libraryReadingTools={useFlags().libraryReadingTools}
                ltiKind={this.props.ltiKind}
                modelType={form.getState().values.type}
                newRecord={this.props.newRecord}
              />

              <NotesSection />
            </div>
          </div>
          <SubmitError error={submitError} />
        </div>

        <Footer
          disableWithTooltipText="Please complete all required fields marked with *."
          primaryButtonDisabled={
            hasValidationErrors || (hasSubmitErrors && !dirtySinceLastSubmit)
          }
          primaryButtonText="Assign"
          secondaryButtonCallback={this.props.closeModal}
          submitting={submitting}
          wrapperClassName={styles.footer}
        >
          {this.state.showLmsInfo && this.renderDraftMessage()}
        </Footer>
      </form>
    );
  }

  render() {
    return (
      <Form
        keepDirtyOnReinitialize
        initialValues={this.getInitialValues()}
        mutators={{
          changeValue: AssignmentForm.formChangeValue,
          handleSelect: AssignmentForm.formHandleSelect
        }}
        render={this.renderForm}
        validate={AssignmentForm.validate}
        onSubmit={this.handleSubmit}
      />
    );
  }
}
